// src/pages/ApprovalsPage.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';
import { fromZonedTime } from 'date-fns-tz';
import { Button, DangerButton } from '../ui';
import { useCountry } from '../context/CountryContext'; // ✅ add this

const client = createAuditedClient('ApprovalsPage');

/* ================= GraphQL ================= */
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items { id name country }
      nextToken
    }
  }
`;

const ListMembers = /* GraphQL */ `
  query ListMembers($CompanyId: String!) {
    listMembers(
      limit: 1000
      filter: { CompanyId: { eq: $CompanyId } }
    ) {
      items {
        id
        CompanyId
        sub
        email
        role
      }
      nextToken
    }
  }
`;


const ListCars = /* GraphQL */ `
  query ListCars {
    listCars(limit: 1000) {
      items { id name plate CompanyId }
      nextToken
    }
  }
`;

/** ChangeRequests (PENDING) */
const PendingCR = /* GraphQL */ `
  query PendingCR {
    listChangeRequests(limit: 1000, filter: { status: { eq: PENDING } }) {
      items {
        id
        CompanyId
        entity
        op
        targetId
        payload
        submittedBySub
        submittedByEmail
        createdAt
      }
      nextToken
    }
  }
`;

/** Live Reservations/Maintenance awaiting approval (approvalStatus=PENDING) */
const PendingReservations = /* GraphQL */ `
  query PendingReservations {
    listReservations(limit: 1000, filter: { approvalStatus: { eq: PENDING } }) {
      items {
        id CompanyId carId originalCarId originalCarName originalCarPlate status title start end pickupAt pickupLocation dropoffAt dropoffLocation
        reservationDate reservationRef customerName flightNumber phoneNumbers babyCarSeat
        depositAmount rentalAmount platform senderEmail notes approvalStatus createdAt updatedAt
      }
      nextToken
    }
  }
`;

const PendingMaintenance = /* GraphQL */ `
  query PendingMaintenance {
    listMaintenances(limit: 1000, filter: { approvalStatus: { eq: PENDING } }) {
      items {
        id CompanyId carId title start end notes approvalStatus createdAt updatedAt
      }
      nextToken
    }
  }
`;

/** For “before” snapshots on CRs */
const GetCar = /* GraphQL */ `
  query GetCar($id: ID!) {
    getCar(id: $id) {
      id CompanyId name plate active fuelType transmission year category bodyType createdAt updatedAt
    }
  }
`;
const GetReservation = /* GraphQL */ `
  query GetReservation($id: ID!) {
    getReservation(id: $id) {
      id CompanyId carId originalCarId originalCarName originalCarPlate status title start end pickupAt pickupLocation dropoffAt dropoffLocation
      reservationDate reservationRef customerName flightNumber phoneNumbers babyCarSeat
      depositAmount rentalAmount platform senderEmail acceptUrl declineUrl notes approvalStatus
      createdAt updatedAt
    }
  }
`;
const GetMaintenance = /* GraphQL */ `
  query GetMaintenance($id: ID!) {
    getMaintenance(id: $id) {
      id CompanyId carId title start end notes approvalStatus createdAt updatedAt
    }
  }
`;

/** Approvals / rejections */
const ApproveChange = /* GraphQL */ `
  mutation ApproveChange($id: ID!) {
    approveChange(id: $id) { id status }
  }
`;
const RejectChange = /* GraphQL */ `
  mutation RejectChange($id: ID!, $reason: String) {
    rejectChange(id: $id, reason: $reason) { id status reason }
  }
`;

const ApproveReservation = /* GraphQL */ `
  mutation ApproveReservation($id: ID!) {
    approveReservation(id: $id) { id approvalStatus }
  }
`;
const RejectReservation = /* GraphQL */ `
  mutation RejectReservation($id: ID!, $reason: String) {
    rejectReservation(id: $id, reason: $reason) { id approvalStatus }
  }
`;

const ApproveMaintenance = /* GraphQL */ `
  mutation ApproveMaintenance($id: ID!) {
    approveMaintenance(id: $id) { id approvalStatus }
  }
`;
const RejectMaintenance = /* GraphQL */ `
  mutation RejectMaintenance($id: ID!, $reason: String) {
    rejectMaintenance(id: $id, reason: $reason) { id approvalStatus }
  }
`;

/* ================= Utils ================= */
function safeParseJSON(x) {
  if (x == null) return null;
  let v = x;
  for (let i = 0; i < 2; i++) {
    if (typeof v === 'string') {
      try {
        v = JSON.parse(v);
        continue;
      } catch {
        break;
      }
    }
    break;
  }
  return (typeof v === 'object' || Array.isArray(v)) ? v : null;
}

function tzForCompanyCountry(country) {
  const c = String(country || '').trim().toUpperCase();
  if (c === 'GE' || c === 'GEO' || c === 'GEORGIA') return 'Asia/Tbilisi';
  if (c === 'AL' || c === 'ALB' || c === 'ALBANIA') return 'Europe/Tirane';
  if (c === 'LB' || c === 'LBN' || c === 'LEBANON') return 'Asia/Beirut';
  return 'UTC';
}

function parseDateTimeValue(value, timeZone = 'UTC') {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  if (!s) return null;

  const hasTZ = /([zZ]|[+\-]\d{2}:\d{2})$/.test(s);
  const d = hasTZ ? new Date(s) : fromZonedTime(s, timeZone || 'UTC');
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTimeValue(value, timeZone = 'UTC') {
  const d = parseDateTimeValue(value, timeZone);
  if (!d) return value == null ? '--' : String(value);

  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone || 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);

    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);

    setMatches(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

const FIELD_LABELS = {
  // Common
  id: 'ID',
  CompanyId: 'Company',
  createdAt: 'Created',
  updatedAt: 'Updated',
  // Car
  name: 'Name',
  plate: 'Plate',
  active: 'Active',
  fuelType: 'Fuel',
  transmission: 'Transmission',
  year: 'Year',
  category: 'Category',
  bodyType: 'Body',
  // Reservation
  carId: 'Car',
  originalCarId: 'Original Car',
  originalCarName: 'Original Car Name',
  originalCarPlate: 'Original Car Plate',
  status: 'Status',
  title: 'Title',
  start: 'Start',
  end: 'End',
  pickupAt: 'Pickup Time',
  pickupLocation: 'Pickup Location',
  dropoffAt: 'Dropoff Time',
  dropoffLocation: 'Dropoff Location',
  reservationDate: 'Reservation Date',
  reservationRef: 'Reservation Ref',
  customerName: 'Customer',
  flightNumber: 'Flight',
  phoneNumbers: 'Phones',
  babyCarSeat: 'Baby Seat',
  depositAmount: 'Deposit',
  rentalAmount: 'Rental',
  platform: 'Platform',
  senderEmail: 'Sender Email',
  acceptUrl: 'Accept URL',
  declineUrl: 'Decline URL',
  notes: 'Notes',
  approvalStatus: 'Approval',
};

function norm(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v.map(norm);
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return v;
  return v;
}
function pretty(v, timeZone = 'UTC') {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v))) {
    return formatDateTimeValue(v, timeZone);
  }
  return String(v);
}
function fieldOrderForEntity(entity) {
  if (entity === 'CAR') {
    return [
      'name',
      'plate',
      'active',
      'fuelType',
      'transmission',
      'year',
      'category',
      'bodyType',
      'CompanyId',
      'createdAt',
      'updatedAt',
    ];
  }
  if (entity === 'RESERVATION') {
    return [
      'carId',
      'originalCarId',
      'originalCarName',
      'originalCarPlate',
      'status',
      'title',
      'start',
      'end',
      'pickupAt',
      'pickupLocation',
      'dropoffAt',
      'dropoffLocation',
      'reservationDate',
      'reservationRef',
      'customerName',
      'flightNumber',
      'phoneNumbers',
      'babyCarSeat',
      'depositAmount',
      'rentalAmount',
      'platform',
      'senderEmail',
      'notes',
      'approvalStatus',
      'CompanyId',
      'createdAt',
      'updatedAt',
    ];
  }
  if (entity === 'MAINTENANCE') {
    return ['carId', 'title', 'start', 'end', 'notes', 'approvalStatus', 'CompanyId', 'createdAt', 'updatedAt'];
  }

  return [];
}
function computeDiff(entity, beforeObj, afterObj) {
  const keys = new Set([
    ...Object.keys(beforeObj || {}),
    ...Object.keys(afterObj || {}),
  ]);
  const ordered = fieldOrderForEntity(entity);
  const sorted = [...keys].sort((a, b) => {
    const ia = ordered.indexOf(a),
      ib = ordered.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
  const rows = [];
  for (const k of sorted) {
    const before = norm(beforeObj?.[k]);
    const after = norm(afterObj?.[k]);
    const changed = JSON.stringify(before) !== JSON.stringify(after);
    rows.push({ key: k, label: FIELD_LABELS[k] || k, before, after, changed });
  }
  return rows;
}

function Badge({ children, color = '#0d6efd' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        background: color,
        color: '#fff',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function DiffTable({ entity, before, after, carNameMap, companyNameMap, timeZone = 'UTC' }) {
  const rows = useMemo(() => computeDiff(entity, before, after), [entity, before, after]);
  const isMobile = useMediaQuery('(max-width: 640px)');

  function prettyWithLookups(k, v) {
    if (k === 'carId' && v) return carNameMap.get(String(v)) || String(v);
    if (k === 'originalCarId' && v) return carNameMap.get(String(v)) || String(v);
    if (k === 'CompanyId' && v) return companyNameMap.get(String(v)) || String(v);
    if (k === 'phoneNumbers' && Array.isArray(v)) return v.join(', ');
    return pretty(v, timeZone);
  }

  if (isMobile) {
    return (
      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 10,
          overflow: 'hidden',
          display: 'grid',
          gap: 0,
        }}
      >
        {rows.map((r) => (
          <div
            key={r.key}
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #f2f2f2',
              background: r.changed ? 'rgba(34,197,94,0.08)' : '#ffffff',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{r.label}</div>
            <div style={{ display: 'grid', gap: 6 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Before
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: '#4b5563' }}>{prettyWithLookups(r.key, r.before)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  After
                </div>
                <div style={{ marginTop: 2, fontSize: 12, color: '#111827', fontWeight: r.changed ? 700 : 400 }}>
                  {prettyWithLookups(r.key, r.after)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #eee',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{ minWidth: 520 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '150px 1fr 1fr',
              background: '#fafafa',
              fontWeight: 600,
              padding: '8px 10px',
              borderBottom: '1px solid #eee',
              fontSize: 12,
            }}
          >
            <div>Field</div>
            <div>Before</div>
            <div>After</div>
          </div>
          <div>
            {rows.map((r) => (
              <div
                key={r.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 1fr 1fr',
                  padding: '6px 10px',
                  borderBottom: '1px solid #f2f2f2',
                  background: r.changed ? 'rgba(34,197,94,0.08)' : 'transparent',
                  fontSize: 12,
                }}
              >
                <div style={{ color: r.changed ? '#111' : '#777' }}>{r.label}</div>
                <div style={{ color: '#444' }}>{prettyWithLookups(r.key, r.before)}</div>
                <div
                  style={{
                    color: '#111',
                    fontWeight: r.changed ? 600 : 400,
                  }}
                >
                  {prettyWithLookups(r.key, r.after)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Toast + Confirm ================= */

function ToastBox({ toast, onClose }) {
  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 360,
      }}
    >
      <div
        style={{
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          boxShadow: '0 10px 25px rgba(15,23,42,0.18)',
          border: '1px solid rgba(148,163,184,0.5)',
          background:
            toast.variant === 'success'
              ? '#e7f9ec'
              : toast.variant === 'error'
              ? '#fef2f2'
              : '#f9fafb',
          color:
            toast.variant === 'success'
              ? '#166534'
              : toast.variant === 'error'
              ? '#991b1b'
              : '#111827',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 16 }}>
          {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '!' : 'ℹ'}
        </div>
        <div style={{ flex: 1 }}>{toast.message}</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            margin: 0,
            fontSize: 14,
            lineHeight: 1,
            color: 'inherit',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ state, onResult }) {
  if (!state) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.35)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={() => onResult(false)}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 14,
          padding: 16,
          minWidth: 280,
          maxWidth: 420,
          boxShadow: '0 20px 40px rgba(15,23,42,0.35)',
          border: '1px solid #e5e7eb',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 style={{ margin: '0 0 6px', fontSize: 16 }}>{state.title}</h4>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#4b5563' }}>
          {state.message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => onResult(false)} style={{ background: 'white' }}>
            {state.cancelLabel}
          </Button>
          <Button
            onClick={() => onResult(true)}
            style={{ background: state.danger ? '#dc2626' : '#83d466' }}
          >
            {state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ================= Shared card style ================= */

function cardStyle() {
  return {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    boxSizing: 'border-box',
  };
}

/* ================= Main Page ================= */
export default function ApprovalsPage() {
  const [claims, setClaims] = useState(null);
  const [ready, setReady] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // groups come from claims set in auth effect
  const isAdmin = useMemo(() => !!claims?.groups?.includes('admin'), [claims]);

  // ✅ Country context (navbar toggle)
  const { selectedCompanyId: ctxCompanyId } = useCountry();

  // ✅ Which company this page should operate on
  const activeCompanyId = useMemo(
    () => (isAdmin ? (ctxCompanyId || claims?.CompanyId || null) : (claims?.CompanyId || null)),
    [isAdmin, ctxCompanyId, claims]
  );

const [companies, setCompanies] = useState([]);
const companyById = useMemo(
  () => new Map(companies.map((c) => [c.id, c])),
  [companies]
);
const companyNameMap = useMemo(
  () => new Map(companies.map((c) => [c.id, c.name])),
  [companies]
);
const companyTimeZone = useCallback(
  (companyId) => {
    const co = companyById.get(companyId);
    return tzForCompanyCountry(co?.country);
  },
  [companyById]
);

const [cars, setCars] = useState([]);
const carNameMap = useMemo(() => {
  const map = new Map();
  for (const c of cars) {
    map.set(c.id, `${c.name}${c.plate ? ` (${c.plate})` : ''}`);
  }
  return map;
}, [cars]);

const [members, setMembers] = useState([]);
const memberBySub = useMemo(() => {
  const map = new Map();
  for (const m of members) {
    if (m.sub) {
      map.set(m.sub, m.email || m.sub);
    }
  }
  return map;
}, [members]);

  const [pendingCR, setPendingCR] = useState([]);
  const [pendingRes, setPendingRes] = useState([]);
  const [pendingMt, setPendingMt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [rawOpen, setRawOpen] = useState(new Set());

  // toast + confirm state
  const [toast, setToast] = useState(null); // { message, variant }
  const toastTimeoutRef = useRef(null);

  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, cancelLabel, danger }
  const confirmResolveRef = useRef(null);

  function showAlert(message, variant = 'info') {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 3500);
  }

  function hideAlert() {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }

  function openConfirm(options) {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title: options.title || 'Confirm action',
        message: options.message || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        danger: !!options.danger,
      });
    });
  }

  function handleConfirm(result) {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(result);
      confirmResolveRef.current = null;
    }
    setConfirmState(null);
  }

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // auth
  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};
        const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
        const groups = p['cognito:groups'] || [];
        setClaims({ CompanyId, groups, sub: p.sub || null, email: p.email || null });
      } catch (e) {
        console.error('ApprovalsPage auth failed', e);
        setClaims({ CompanyId: null, groups: [] });
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // base lists (companies & cars)
  // base lists (companies & cars) – scoped by activeCompanyId
  useEffect(() => {
  if (!ready || !isAdmin || !activeCompanyId) return;

  (async () => {
    try {
      const [coRes, carRes, memRes] = await Promise.all([
        client.graphql({ query: ListCompanies, authMode: 'userPool' }),
        client.graphql({ query: ListCars, authMode: 'userPool' }),
        client.graphql({
          query: ListMembers,
          variables: { CompanyId: activeCompanyId },
          authMode: 'userPool',
        }),
      ]);

      const allCompanies = (coRes.data?.listCompanies?.items || []).filter(Boolean);
      const allCars = (carRes.data?.listCars?.items || []).filter(Boolean);
      const allMembers = (memRes.data?.listMembers?.items || []).filter(Boolean);

      setCompanies(allCompanies.filter((c) => c.id === activeCompanyId));
      setCars(allCars.filter((c) => c.CompanyId === activeCompanyId));
      setMembers(allMembers);
    } catch (e) {
      console.error('Base lists load failed', e);
      showAlert('Failed to load base lists. Check console/logs.', 'error');
      setCompanies([]);
      setCars([]);
      setMembers([]);
    }
  })();
}, [ready, isAdmin, activeCompanyId]);



  const fetchBefore = useCallback(async (cr) => {
    if (cr.op === 'CREATE') return null;
    if (!cr.targetId) return null;
    if (cr.entity === 'CAR') {
      const res = await client.graphql({
        query: GetCar,
        variables: { id: cr.targetId },
        authMode: 'userPool',
      });
      return res?.data?.getCar || null;
    }
    if (cr.entity === 'RESERVATION') {
      const res = await client.graphql({
        query: GetReservation,
        variables: { id: cr.targetId },
        authMode: 'userPool',
      });
      return res?.data?.getReservation || null;
    }
    if (cr.entity === 'MAINTENANCE') {
      const res = await client.graphql({
        query: GetMaintenance,
        variables: { id: cr.targetId },
        authMode: 'userPool',
      });
      return res?.data?.getMaintenance || null;
    }
    return null;
  }, []);

  const reload = useCallback(async () => {
    if (!isAdmin || !activeCompanyId) {
      setPendingCR([]);
      setPendingRes([]);
      setPendingMt([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // ChangeRequests
      const crRes = await client.graphql({ query: PendingCR, authMode: 'userPool' });
      let crItems = (crRes.data?.listChangeRequests?.items || []).filter(Boolean);

      // ✅ keep only this company
      crItems = crItems.filter(cr => cr.CompanyId === activeCompanyId);

      const enriched = await Promise.all(
        crItems.map(async (cr) => {
          const afterRaw = safeParseJSON(cr.payload) || {};
          const before = await fetchBefore(cr);
          const after = cr.op === 'DELETE' ? null : afterRaw;

          if (after?.id && cr.entity === 'CAR') {
            setCars((prev) =>
              prev.some((p) => p.id === after.id) ? prev : [...prev, after]
            );
          }
          return { ...cr, _before: before, _after: after };
        })
      );
      setPendingCR(enriched);

      // Pending Reservations
      const resRes = await client.graphql({
        query: PendingReservations,
        authMode: 'userPool',
      });
      const resItems = (resRes.data?.listReservations?.items || [])
        .filter(Boolean)
        .filter(r => r.CompanyId === activeCompanyId); // ✅ filter

      setPendingRes(resItems);

      // Pending Maintenance
      const mtRes = await client.graphql({
        query: PendingMaintenance,
        authMode: 'userPool',
      });
      const mtItems = (mtRes.data?.listMaintenances?.items || [])
        .filter(Boolean)
        .filter(m => m.CompanyId === activeCompanyId); // ✅ filter

      setPendingMt(mtItems);
    } catch (e) {
      console.error('Approvals reload failed', e);
      setPendingCR([]);
      setPendingRes([]);
      setPendingMt([]);
      showAlert('Failed to reload approvals. Check console/logs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, activeCompanyId, fetchBefore]);


  useEffect(() => {
    if (ready) reload();
  }, [ready, reload]);

  async function approveCR(cr) {
    const confirm = await openConfirm({
      title: 'Approve change request',
      message: `Approve ${cr.entity} ${cr.op.toLowerCase()} for company ${cr.CompanyId}?`,
      confirmLabel: 'Approve',
      cancelLabel: 'Cancel',
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: ApproveChange,
        variables: { id: cr.id },
        authMode: 'userPool',
      });
      showAlert('Change request approved.', 'success');
      await reload();
    } catch (e) {
      console.error('approve CR failed', e);
      showAlert('Approve failed. Check console.', 'error');
    }
  }

  async function rejectCR(cr) {
    const confirm = await openConfirm({
      title: 'Reject change request',
      message: `Reject ${cr.entity} ${cr.op.toLowerCase()} for company ${cr.CompanyId}?`,
      confirmLabel: 'Reject',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: RejectChange,
        variables: { id: cr.id, reason: null }, // reason optional; skip UI
        authMode: 'userPool',
      });
      showAlert('Change request rejected.', 'success');
      await reload();
    } catch (e) {
      console.error('reject CR failed', e);
      showAlert('Reject failed. Check console.', 'error');
    }
  }

  async function approveRes(row) {
    const confirm = await openConfirm({
      title: 'Approve reservation',
      message: `Approve reservation for ${row.customerName || 'customer'}?`,
      confirmLabel: 'Approve',
      cancelLabel: 'Cancel',
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: ApproveReservation,
        variables: { id: row.id },
        authMode: 'userPool',
      });
      showAlert('Reservation approved.', 'success');
      await reload();
    } catch (e) {
      console.error('approve reservation failed', e);
      showAlert('Approve failed. Check console.', 'error');
    }
  }

  async function rejectRes(row) {
    const confirm = await openConfirm({
      title: 'Reject reservation',
      message: `Reject reservation for ${row.customerName || 'customer'}?`,
      confirmLabel: 'Reject',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: RejectReservation,
        variables: { id: row.id, reason: null },
        authMode: 'userPool',
      });
      showAlert('Reservation rejected.', 'success');
      await reload();
    } catch (e) {
      console.error('reject reservation failed', e);
      showAlert('Reject failed. Check console.', 'error');
    }
  }

  async function approveMt(row) {
    const confirm = await openConfirm({
      title: 'Approve maintenance',
      message: `Approve maintenance "${row.title || ''}"?`,
      confirmLabel: 'Approve',
      cancelLabel: 'Cancel',
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: ApproveMaintenance,
        variables: { id: row.id },
        authMode: 'userPool',
      });
      showAlert('Maintenance approved.', 'success');
      await reload();
    } catch (e) {
      console.error('approve maintenance failed', e);
      showAlert('Approve failed. Check console.', 'error');
    }
  }

  async function rejectMt(row) {
    const confirm = await openConfirm({
      title: 'Reject maintenance',
      message: `Reject maintenance "${row.title || ''}"?`,
      confirmLabel: 'Reject',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!confirm) return;

    try {
      await client.graphql({
        query: RejectMaintenance,
        variables: { id: row.id, reason: null },
        authMode: 'userPool',
      });
      showAlert('Maintenance rejected.', 'success');
      await reload();
    } catch (e) {
      console.error('reject maintenance failed', e);
      showAlert('Reject failed. Check console.', 'error');
    }
  }

  const groupedCR = useMemo(() => {
    const g = { CAR: [], RESERVATION: [], MAINTENANCE: [] };
    for (const cr of pendingCR) {
      if (g[cr.entity]) g[cr.entity].push(cr);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    }
    return g;
  }, [pendingCR]);

  function CRSection({ title, items }) {
  return (
    <section style={{ display: 'grid', gap: 12 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>
        {title}{' '}
        {items.length ? <Badge color="#6366f1">{items.length}</Badge> : null}
      </h3>
      {items.length === 0 && (
        <div style={{ color: '#666', fontSize: 13 }}>(None)</div>
      )}
      <div style={{ display: 'grid', gap: 12 }}>
        {items.map((cr) => {
          const key = cr.id;
          const isOpen = expanded.has(key);
          const showRaw = rawOpen.has(key);

          const opColor =
            cr.op === 'CREATE'
              ? '#10b981'
              : cr.op === 'UPDATE'
              ? '#f59e0b'
              : '#ef4444';

          const coName = companyNameMap.get(cr.CompanyId) || cr.CompanyId;

          // 🔥 Prefer submittedByEmail, then Member.email, avoid showing the ugly sub
          const requester =
            cr.submittedByEmail ||
            memberBySub.get(cr.submittedBySub) ||
            'Unknown user';

          const kindLabel = (() => {
            if (cr.entity === 'RESERVATION') {
              if (cr.op === 'CREATE') return 'New reservation';
              if (cr.op === 'UPDATE') return 'Edit reservation';
              if (cr.op === 'DELETE') return 'Delete reservation';
            }
            if (cr.entity === 'CAR') {
              if (cr.op === 'CREATE') return 'New car';
              if (cr.op === 'UPDATE') return 'Edit car';
              if (cr.op === 'DELETE') return 'Delete car';
            }
            if (cr.entity === 'MAINTENANCE') {
              if (cr.op === 'CREATE') return 'New maintenance record';
              if (cr.op === 'UPDATE') return 'Edit maintenance record';
              if (cr.op === 'DELETE') return 'Delete maintenance record';
            }
            return null;
          })();

          return (
            <div
              key={cr.id}
              style={{
                border: '1px solid #eee',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: 10,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  background: '#fafafa',
                  borderBottom: '1px solid #eee',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    flexWrap: 'wrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {/* left side badges + meta */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                      minWidth: 0,
                    }}
                  >
                    <Badge color="#334155">{cr.entity}</Badge>
                    <Badge color={opColor}>{cr.op}</Badge>
                    {kindLabel && (
                      <div
                        style={{
                          color: '#111827',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {kindLabel}
                      </div>
                    )}
                    <div
                      style={{
                        color: '#333',
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {coName}
                    </div>
                    {cr.targetId && (
                      <div style={{ color: '#666', fontSize: 11 }}>
                        target: {cr.targetId}
                      </div>
                    )}
                    <div style={{ color: '#666', fontSize: 11 }}>
                      {formatDateTimeValue(cr.createdAt, companyTimeZone(cr.CompanyId))}
                    </div>
                  </div>

                  {/* requester line */}
                  <div
                    style={{
                      fontSize: 11,
                      color: '#4b5563',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>Requested by:</span>
                    <span>{requester}</span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  <Button
                    onClick={() => {
                      const ns = new Set(expanded);
                      ns.has(key) ? ns.delete(key) : ns.add(key);
                      setExpanded(ns);
                    }}
                    style={{ background: 'white', flex: isMobile ? '1 1 120px' : undefined }}
                  >
                    {isOpen ? 'Hide' : 'Review'}
                  </Button>
                  <DangerButton onClick={() => rejectCR(cr)} style={{ flex: isMobile ? '1 1 120px' : undefined }}>Reject</DangerButton>
                  <Button onClick={() => approveCR(cr)} style={{ flex: isMobile ? '1 1 120px' : undefined }}>Approve</Button>
                </div>
              </div>

              {isOpen && (
                <div
                  style={{
                    padding: 12,
                    display: 'grid',
                    gap: 10,
                  }}
                >
                  <DiffTable
                    entity={cr.entity}
                    before={cr.op === 'CREATE' ? null : cr._before}
                    after={cr.op === 'DELETE' ? null : cr._after}
                    carNameMap={carNameMap}
                    companyNameMap={companyNameMap}
                    timeZone={companyTimeZone(cr.CompanyId)}
                  />
                  <div>
                    <Button
                      onClick={() => {
                        const ns = new Set(rawOpen);
                        ns.has(key) ? ns.delete(key) : ns.add(key);
                        setRawOpen(ns);
                      }}
                      style={{ background: 'white' }}
                    >
                      {showRaw ? 'Hide raw' : 'Show raw'}
                    </Button>
                  </div>
                  {showRaw && (
                    <pre
                      style={{
                        background: '#0b1020',
                        color: '#eaf2ff',
                        padding: 12,
                        borderRadius: 8,
                        overflow: 'auto',
                        fontSize: 12,
                      }}
                    >
                      {JSON.stringify(safeParseJSON(cr.payload), null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}



  function SimpleItem({ row, companyName, carName, onApprove, onReject, meta }) {
    return (
      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 12,
          padding: 10,
          display: 'grid',
          gap: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Badge color="#f59e0b">PENDING</Badge>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {carName || '—'}
            </div>
            <div style={{ color: '#666', fontSize: 12 }}>{companyName}</div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <DangerButton onClick={() => onReject(row)} style={{ flex: isMobile ? '1 1 120px' : undefined }}>Reject</DangerButton>
            <Button onClick={() => onApprove(row)} style={{ flex: isMobile ? '1 1 120px' : undefined }}>Approve</Button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#333' }}>{meta}</div>
      </div>
    );
  }

  function ReservationsSection() {
    // IDs of reservations that already have a ChangeRequest
    const crTargets = new Set(
      (groupedCR.RESERVATION || [])
        .filter((cr) => cr.targetId)
        .map((cr) => String(cr.targetId))
    );

    const items = pendingRes
      .filter((r) => !crTargets.has(String(r.id))) // hide ones that have a CR
      .slice()
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));

    return (
      <section style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>
          Reservations{' '}
          {items.length ? <Badge color="#0ea5e9">{items.length}</Badge> : null}
        </h3>
        {items.length === 0 && (
          <div style={{ color: '#666', fontSize: 13 }}>(None)</div>
        )}
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((row) => {
            const companyName =
              companyNameMap.get(row.CompanyId) || row.CompanyId;
            const carName = carNameMap.get(row.carId) || row.carId;
            const tz = companyTimeZone(row.CompanyId);
            const meta = `${formatDateTimeValue(row.start, tz)} → ${formatDateTimeValue(
              row.end,
              tz
            )} • ${row.customerName || ''}${
              row.reservationRef ? ` • ${row.reservationRef}` : ''
            }`;
            return (
              <SimpleItem
                key={row.id}
                row={row}
                companyName={companyName}
                carName={carName}
                meta={meta}
                onApprove={approveRes}
                onReject={rejectRes}
              />
            );
          })}
        </div>
      </section>
    );
  }

  function MaintenanceSection() {
    // IDs of maintenance rows that already have a ChangeRequest
    const crTargets = new Set(
      (groupedCR.MAINTENANCE || [])
        .filter((cr) => cr.targetId)
        .map((cr) => String(cr.targetId))
    );

    const items = pendingMt
      .filter((m) => !crTargets.has(String(m.id)))
      .slice()
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));

    return (
      <section style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>
          Maintenance{' '}
          {items.length ? <Badge color="#f97316">{items.length}</Badge> : null}
        </h3>
        {items.length === 0 && (
          <div style={{ color: '#666', fontSize: 13 }}>(None)</div>
        )}
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((row) => {
            const companyName =
              companyNameMap.get(row.CompanyId) || row.CompanyId;
            const carName = carNameMap.get(row.carId) || row.carId;
            const tz = companyTimeZone(row.CompanyId);
            const meta = `${formatDateTimeValue(row.start, tz)} → ${formatDateTimeValue(
              row.end,
              tz
            )}${
              row.title ? ` • ${row.title}` : ''
            }`;
            return (
              <SimpleItem
                key={row.id}
                row={row}
                companyName={companyName}
                carName={carName}
                meta={meta}
                onApprove={approveMt}
                onReject={rejectMt}
              />
            );
          })}
        </div>
      </section>
    );
  }

  if (!ready) {
    return (
      <div style={{ padding: 16 }}>
        Loading…
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && (
          <ConfirmDialog state={confirmState} onResult={handleConfirm} />
        )}
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 16 }}>
        <b>Not authorized.</b> Approvals are admin-only.
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && (
          <ConfirmDialog state={confirmState} onResult={handleConfirm} />
        )}
      </div>
    );
  }

  if (isAdmin && !activeCompanyId) {
    return (
      <div style={{ padding: 16 }}>
        <b>Select an agency.</b> Use the country/agency toggle in the navbar to choose
        which approvals you want to review.
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && (
          <ConfirmDialog state={confirmState} onResult={handleConfirm} />
        )}
      </div>
    );
  }


  return (
    <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)' }}>
      <div
        style={{
          width: '100%',
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '12px 10px 24px' : '16px 12px 32px',
          display: 'grid',
          gap: 18,
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 22 }}>Approvals</h3>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 13,
                color: '#6b7280',
              }}
            >
              Review and approve change requests, reservations, and maintenance coming
              from agencies.
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button onClick={reload} style={{ background: 'white' }}>
              Refresh
            </Button>
            {loading && (
              <span style={{ color: '#6b7280', fontSize: 12 }}>Loading…</span>
            )}
            <span
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                padding: '4px 8px',
                borderRadius: 999,
                border: '1px solid #d1d5db',
                background: '#e5f7da',
                color: '#166534',
                whiteSpace: 'nowrap',
              }}
            >
              Admin only
            </span>
          </div>
        </header>

        {/* Change requests card */}
        <section style={cardStyle()}>
          <h4 style={{ margin: 0, fontSize: 16 }}>Change requests</h4>
          <p
            style={{
              margin: '4px 0 12px',
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Car edits, reservation changes, maintenance updates and entries submitted by
            agency admins.
          </p>
          <div style={{ display: 'grid', gap: 16 }}>
            <CRSection title="Cars" items={groupedCR.CAR} />
            <CRSection title="Reservations" items={groupedCR.RESERVATION} />
            <CRSection title="Maintenance" items={groupedCR.MAINTENANCE} />
          </div>
        </section>

        {/* Reservations approvals */}
        <section style={cardStyle()}>
          <h4 style={{ margin: 0, fontSize: 16 }}>Reservations awaiting approval</h4>
          <p
            style={{
              margin: '4px 0 12px',
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Reservations that are live on the calendar but still waiting for your
            decision.
          </p>
          <ReservationsSection />
        </section>

        {/* Maintenance approvals */}
        <section style={cardStyle()}>
          <h4 style={{ margin: 0, fontSize: 16 }}>Maintenance awaiting approval</h4>
          <p
            style={{
              margin: '4px 0 12px',
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Maintenance items created/edited by agency admins that still need approval.
          </p>
          <MaintenanceSection />
        </section>
      </div>

      {/* Toast + Confirm overlays */}
      {toast && <ToastBox toast={toast} onClose={hideAlert} />}
      {confirmState && (
        <ConfirmDialog state={confirmState} onResult={handleConfirm} />
      )}
    </div>
  );
}
